import type { SessionService } from '@efdigital/ll-addon-auth';
import {
  buildLookupUsersRequest,
  type LookupUser,
  parseLookupUsersResponse,
} from '@efdigital/ll-admin-v2/data/builders/users/lookup-users';
import Service, { service } from '@ember/service';

export type { LookupUser } from '@efdigital/ll-admin-v2/data/builders/users/lookup-users';

export type UserLookupMap = Record<string, LookupUser>;

export default class UserLookupService extends Service {
  @service declare session: SessionService;

  #cache: Record<string, LookupUser> = {};

  /**
   * Lookup users from the API.
   * @param userIds - The IDs of the users to lookup.
   * @returns A map of user IDs to user objects.
   */
  async lookupUsers(userIds: string[]): Promise<UserLookupMap> {
    const uniqueIds = [...new Set(userIds.filter(Boolean))];
    const uncachedIds = uniqueIds.filter((id) => !this.#cache[id]);

    if (uncachedIds.length) {
      const request = await buildLookupUsersRequest(uncachedIds, this.session);
      const response = await fetch(request);
      const results = await parseLookupUsersResponse(response);

      for (const result of results) {
        this.#cache[result.id] = result;
      }
    }

    return uniqueIds.reduce<UserLookupMap>((map, id) => {
      map[id] = this.#cache[id] ?? { id };
      return map;
    }, {});
  }

  /**
   * Lookup a user from the API.
   * @param userId - The ID of the user to lookup.
   * @returns The user object.
   */
  lookupUser = async (userId: string): Promise<LookupUser> => {
    const users = await this.lookupUsers([userId]);
    return users[userId] ?? { id: userId };
  };

  /**
   * Lookup a user from the cache.
   * You must have already awaited `lookupUsers` for this to work.
   * @param userId - The ID of the user to lookup.
   * @returns The user object.
   */
  fromCache = (userId: string): LookupUser => {
    return this.#cache[userId] ?? { id: userId };
  };
}
